/**
 * SeedCredentialsEditor — P7 of `ai-docs/llm-resolver-unification-impl-plan.md`.
 *
 * Per-provider credential cards (one card per `LlmProvider`). Each card:
 *   - shows current state (configured / not configured / last-validated)
 *   - lets the operator paste a new credential (api-key string OR OAuth
 *     JSON blob) and run validate-then-save via
 *     `POST /api/platform/settings/seed-credentials/:provider`.
 *   - displays the `validation.probeRef` + `probeSource` from the success
 *     response so the operator sees which model the ping went against.
 *
 * Replaces the credential half of the deleted `<DefaultLlmChoiceEditor>`.
 * Profile-definition concerns live in the sibling `<LlmProfilesEditor>`.
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type {
  LlmCredential,
  LlmProvider,
  PlatformSeedCredentialMasked,
} from "@/lib/platform-api";
import { PlatformApiError } from "@/lib/platform-api";
import { usePlatformApi } from "@/hooks/usePlatformApi";

/** The 7 LlmProvider enum values. Mirrors `LlmProviderSchema` in
 *  `@lexiun/lx-llm-auth` — keep in sync if the enum grows. */
const ALL_PROVIDERS: LlmProvider[] = [
  "anthropic",
  "openai",
  "openai-codex",
  "google",
  "xai",
  "openrouter",
  "custom",
];

interface CardState {
  busy: boolean;
  // Free-text input — the operator pastes either an api-key string OR a
  // JSON-encoded OAuth payload. The card's `kind` toggle picks which way
  // to parse it.
  input: string;
  kind: "api-key" | "oauth" | "custom";
  // Custom-mode extras.
  baseUrl: string;
  // Validation toast (success or error) — sticky until the next attempt.
  toast: {
    ok: boolean;
    text: string;
    probeRef?: { provider: string; model: string };
    probeSource?: string;
  } | null;
  // Probe-model override for the validate-then-save flow.
  probeModel: string;
}

const EMPTY_CARD: CardState = {
  busy: false,
  input: "",
  kind: "api-key",
  baseUrl: "",
  toast: null,
  probeModel: "",
};

function StatusBadge({
  masked,
}: {
  masked: PlatformSeedCredentialMasked | undefined;
}) {
  if (!masked) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-muted">
        <XCircle className="h-4 w-4 text-slate-400" />
        Not configured
      </span>
    );
  }
  if (masked.lastValidatedOk) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        Configured · validated {new Date(masked.lastValidatedAt ?? masked.setAt).toLocaleString()}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <AlertTriangle className="h-4 w-4 text-amber-500" />
      Configured · last validation failed
    </span>
  );
}

export function SeedCredentialsEditor() {
  const api = usePlatformApi();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [masked, setMasked] = useState<
    Partial<Record<LlmProvider, PlatformSeedCredentialMasked>>
  >({});
  const [cards, setCards] = useState<Record<LlmProvider, CardState>>(() => {
    const init: Record<string, CardState> = {};
    for (const p of ALL_PROVIDERS) init[p] = { ...EMPTY_CARD };
    return init as Record<LlmProvider, CardState>;
  });

  const refetch = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getPlatformSeedCredentials();
      setMasked(res.data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load seed credentials");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const updateCard = useCallback(
    (provider: LlmProvider, patch: Partial<CardState>) => {
      setCards((prev) => ({ ...prev, [provider]: { ...prev[provider], ...patch } }));
    },
    [],
  );

  const handleTestAndSave = useCallback(
    async (provider: LlmProvider) => {
      const card = cards[provider];
      const credential = buildCredential(provider, card);
      if ("error" in credential) {
        updateCard(provider, { toast: { ok: false, text: credential.error } });
        return;
      }
      updateCard(provider, { busy: true, toast: null });
      try {
        const res = await api.setPlatformSeedCredential(
          provider,
          credential.value,
          card.probeModel.trim() || undefined,
        );
        updateCard(provider, {
          busy: false,
          input: "", // clear sensitive value after save
          toast: {
            ok: true,
            text: `Saved. Validated against ${res.data.validation.probeRef.provider}/${res.data.validation.probeRef.model} (${res.data.validation.probeSource}, ${res.data.validation.latencyMs}ms).`,
            probeRef: res.data.validation.probeRef,
            probeSource: res.data.validation.probeSource,
          },
        });
        // Refresh the masked view so the badge flips to "validated …".
        await refetch();
      } catch (err) {
        const validation = (err instanceof PlatformApiError && err.details?.validation) as
          | { code?: string; message?: string }
          | undefined;
        const text =
          validation?.code && validation?.message
            ? `Validation failed (${validation.code}): ${validation.message}`
            : err instanceof Error
            ? err.message
            : "Save failed";
        updateCard(provider, { busy: false, toast: { ok: false, text } });
      }
    },
    [api, cards, refetch, updateCard],
  );

  return (
    <Card data-testid="seed-credentials-editor">
      <CardHeader>
        <CardTitle className="text-lg">Provisioning seed credentials</CardTitle>
        <CardDescription>
          Per-provider credentials new organisations inherit at provision time.
          Each provider is independent — rotating openai doesn't touch anthropic.
          Validation calls a real LLM ping; nothing persists until the ping
          succeeds.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading seed credentials…
          </div>
        ) : loadError ? (
          <div className="text-sm text-red-600 dark:text-red-400">{loadError}</div>
        ) : (
          ALL_PROVIDERS.map((provider) => (
            <div
              key={provider}
              className="border border-slate-200 dark:border-slate-700 rounded-md p-3 space-y-2"
              data-testid={`seed-card-${provider}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{provider}</div>
                <StatusBadge masked={masked[provider]} />
              </div>
              <div className="flex gap-2 text-sm">
                {(["api-key", "oauth", "custom"] as const).map((k) => (
                  <label key={k} className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      name={`kind-${provider}`}
                      checked={cards[provider].kind === k}
                      onChange={() => updateCard(provider, { kind: k })}
                    />
                    {k}
                  </label>
                ))}
              </div>
              {cards[provider].kind === "custom" && (
                <input
                  type="text"
                  placeholder="baseUrl (https://...)"
                  className="w-full rounded border px-2 py-1 text-sm bg-white dark:bg-slate-900"
                  value={cards[provider].baseUrl}
                  onChange={(e) => updateCard(provider, { baseUrl: e.target.value })}
                />
              )}
              <textarea
                placeholder={
                  cards[provider].kind === "oauth"
                    ? `Paste OAuth JSON: {"access":"...","refresh":"...","expires":...}`
                    : cards[provider].kind === "custom"
                    ? "Optional api-key for the custom endpoint"
                    : "Paste API key"
                }
                className="w-full rounded border px-2 py-1 text-sm font-mono bg-white dark:bg-slate-900"
                rows={cards[provider].kind === "oauth" ? 4 : 2}
                value={cards[provider].input}
                onChange={(e) => updateCard(provider, { input: e.target.value })}
              />
              <input
                type="text"
                placeholder="Probe model (optional — server picks from catalog-pin or fallback)"
                className="w-full rounded border px-2 py-1 text-xs bg-white dark:bg-slate-900"
                value={cards[provider].probeModel}
                onChange={(e) => updateCard(provider, { probeModel: e.target.value })}
              />
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  onClick={() => handleTestAndSave(provider)}
                  disabled={cards[provider].busy || !cards[provider].input.trim()}
                >
                  {cards[provider].busy ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Testing…
                    </>
                  ) : (
                    "Test & Save"
                  )}
                </Button>
                {cards[provider].toast && (
                  <span
                    className={`text-xs ${
                      cards[provider].toast!.ok
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-red-700 dark:text-red-400"
                    }`}
                  >
                    {cards[provider].toast!.text}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

/** Parse the card's free-text input into a typed `LlmCredential`. The
 *  per-provider routes' Zod schema (`LlmCredentialSchema`) is the
 *  authoritative validator — this helper only does the cheap structural
 *  parse so we can give immediate UI feedback. */
function buildCredential(
  provider: LlmProvider,
  card: CardState,
): { value: LlmCredential } | { error: string } {
  const input = card.input.trim();
  if (!input) return { error: "Credential input is empty" };
  if (card.kind === "api-key") {
    return { value: { kind: "api-key", provider, apiKey: input } };
  }
  if (card.kind === "oauth") {
    let parsed: { access?: string; refresh?: string; expires?: number };
    try {
      parsed = JSON.parse(input);
    } catch (err) {
      return { error: `OAuth JSON parse failed: ${(err as Error).message}` };
    }
    if (!parsed.access || !parsed.refresh || typeof parsed.expires !== "number") {
      return {
        error: "OAuth JSON must include access (string), refresh (string), expires (number)",
      };
    }
    return {
      value: {
        kind: "oauth",
        provider,
        oauth: {
          type: "oauth",
          provider,
          access: parsed.access,
          refresh: parsed.refresh,
          expires: parsed.expires,
        },
      } as LlmCredential,
    };
  }
  // custom
  const baseUrl = card.baseUrl.trim();
  if (!baseUrl) return { error: "Custom provider requires baseUrl" };
  return {
    value: {
      kind: "custom",
      provider,
      baseUrl,
      apiKey: input || undefined,
    } as LlmCredential,
  };
}
